import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { effectsAPI } from '../services/api';
import toast from 'react-hot-toast';
import EffectModal from './EffectModal';

const EFFECT_TYPES = ["Waterfall", "Mine", "Gerb", "Spark", "Flame", "Comet", "Strobe", "Flash", "Confetti", "Other"];

const EffectsDatabase = ({ onBack }) => {
  const [effects, setEffects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEffect, setEditingEffect] = useState(null);
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    loadEffects();
  }, []);

  const loadEffects = async () => {
    try {
      setLoading(true);
      const response = await effectsAPI.getAll();
      setEffects(response.data);
    } catch (error) {
      toast.error('Failed to load effects');
      console.error('Error loading effects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEffect = () => {
    setEditingEffect(null);
    setShowModal(true);
  };

  const handleEditEffect = (effect) => {
    setEditingEffect(effect);
    setShowModal(true);
  };

  const handleDeleteEffect = async (effectId) => {
    if (!window.confirm('Are you sure you want to delete this effect?')) {
      return;
    }

    try {
      await effectsAPI.delete(effectId);
      toast.success('Effect deleted successfully');
      loadEffects();
    } catch (error) {
      toast.error('Failed to delete effect');
      console.error('Error deleting effect:', error);
    }
  };

  const handleSaveEffect = async (effectData) => {
    try {
      if (editingEffect) {
        await effectsAPI.update(editingEffect.id, effectData);
        toast.success('Effect updated successfully');
      } else {
        console.log('Creating effect with data:', effectData);
        const response = await effectsAPI.create(effectData);
        console.log('Effect created successfully:', response);
        toast.success('Effect created successfully');
      }
      setShowModal(false);
      loadEffects();
    } catch (error) {
      console.error('Detailed error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to save effect');
      console.error('Error saving effect:', error);
    }
  };

  const filteredEffects = effects.filter(effect => 
    !filterType || effect.type === filterType
  );

  if (loading) {
    return (
      <div className="text-center">
        <p>Loading effects...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h2>Effects Database</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button className="btn" onClick={handleCreateEffect}>
              <Plus size={16} className="mr-2" />
              New Effect
            </button>
          </div>
        </div>

        <div className="form-group" style={{ maxWidth: '300px', marginBottom: '20px' }}>
          <label>Filter by Type</label>
          <select
            className="form-control"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            {EFFECT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {filteredEffects.length === 0 ? (
          <div className="text-center">
            <p>No effects found. Create your first effect to get started.</p>
            <button className="btn mt-4" onClick={handleCreateEffect}>
              <Plus size={16} className="mr-2" />
              Create Effect
            </button>
          </div>
        ) : (
          <div className="grid grid-3">
            {filteredEffects.map((effect) => (
              <div key={effect.id} className="card" style={{ margin: '0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                  <h3 style={{ margin: '0', fontSize: '18px' }}>{effect.name}</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                      onClick={() => handleEditEffect(effect)}
                      title="Edit Effect"
                    >
                      <Edit size={16} style={{ marginRight: '6px' }} />
                      Edit
                    </button>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                      onClick={() => handleDeleteEffect(effect.id)}
                      title="Delete Effect"
                    >
                      <Trash2 size={16} style={{ marginRight: '6px' }} />
                      Delete
                    </button>
                  </div>
                </div>

                {effect.image_data ? (
                  <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                    <img
                      src={`data:image/png;base64,${effect.image_data}`}
                      alt={effect.name}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '150px', 
                        borderRadius: '4px',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px',
                    marginBottom: '15px'
                  }}>
                    <ImageIcon size={32} style={{ color: '#666' }} />
                    <p style={{ margin: '10px 0 0', color: '#666', fontSize: '14px' }}>No image</p>
                  </div>
                )}

                <div style={{ fontSize: '14px', color: '#666' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Type:</strong> {effect.type}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Range:</strong> {effect.effective_range}m
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Safety Distance:</strong> {effect.safety_distance}m
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Clearance Height:</strong> {effect.clearance_height}m
                  </div>
                  {effect.description && (
                    <div style={{ marginTop: '10px', fontStyle: 'italic' }}>
                      {effect.description}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <EffectModal
          effect={editingEffect}
          onSave={handleSaveEffect}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default EffectsDatabase;